import { useQuery } from "@tanstack/react-query";

import { mutateData } from "@/core/api/api-methods";
import { QUERY_KEYS } from "@/core/api/query-keys";
import { kycEndpoints } from "@/features/kyc/api/endpoints";
import type {
  PartyNameEntry,
  PartyNamesResponse,
} from "@/features/kyc/api/dto";

/**
 * Lookup collectif d'IDs d'identité → noms d'affichage
 * (`POST /Parties/names`, guide v2.3 §6.2).
 *
 * Destiné aux écrans qui affichent des IDs techniques (listes wallet / ledger
 * / merchant). Retourne **uniquement** le `displayName` — jamais de téléphone,
 * email ou n° de pièce. ≤ 200 IDs par appel ; batcher côté UI (debounce).
 * Rôles : flashpay-superadmin, flashpay-admin, flashpay-agents.
 */
const ENVELOPE_KEYS = ["items", "names", "data", "results"] as const;

const isPartyNameEntry = (value: unknown): value is PartyNameEntry =>
  !!value &&
  typeof value === "object" &&
  typeof (value as PartyNameEntry).partyId === "string" &&
  typeof (value as PartyNameEntry).displayName === "string";

/**
 * Le contrat Postman documente un objet unique alors que l'endpoint est un
 * lot : on accepte tableau, entrée seule, enveloppe `{ items | names | data
 * | results }` ou dictionnaire `{ [partyId]: displayName }`.
 */
const toPartyNameEntries = (data: unknown): PartyNameEntry[] => {
  if (Array.isArray(data)) return data.filter(isPartyNameEntry);
  if (!data || typeof data !== "object") return [];
  if (isPartyNameEntry(data)) return [data];

  const record = data as Record<string, unknown>;
  for (const key of ENVELOPE_KEYS) {
    if (record[key] !== undefined) return toPartyNameEntries(record[key]);
  }
  return Object.entries(record)
    .filter((entry): entry is [string, string] => typeof entry[1] === "string")
    .map(([partyId, displayName]) => ({ partyId, displayName }));
};

export const usePartyNames = (partyIds: readonly string[], enabled = true) => {
  const ids = partyIds.filter(Boolean);
  const query = useQuery({
    queryKey: QUERY_KEYS.KYC_PARTY_NAMES(ids),
    queryFn: async (): Promise<PartyNamesResponse> =>
      toPartyNameEntries(
        await mutateData({
          url: kycEndpoints.parties.names,
          reqBody: { partyIds: ids },
          method: "POST",
        })
      ),
    enabled: enabled && ids.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  const nameByPartyId = new Map<string, string>();
  for (const entry of query.data ?? []) {
    if (entry.partyId && entry.displayName) {
      nameByPartyId.set(entry.partyId, entry.displayName);
    }
  }

  return { ...query, nameByPartyId };
};
